import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { useAtomValue } from 'jotai';
import { get } from 'underscore';
import {
  GEOJSON_FEATURE,
  GEOJSON_FEATURE_COLLECTION,
  GEOJSON_POINT,
  GEOJSON_POLYGON,
} from 'admin/engagement/components/GeolocationQuestionInput/helper';
import { useMapboxMap, registerCustomPinImages } from '../../hooks';
import { iconImageForMapLayer } from '../../markers';
import { drawerStateAtom } from '../../store';
import EmptyResults from '../EmptyResults';

const SHAPE_COLOR = '#1a56c4';
const SHAPE_FILL_OPACITY = 0.3;
const SHAPE_OUTLINE_WIDTH = 1;

function MapDrawShapeQuestionResults(props) {
  const { question, hasResponses } = props;
  const { data } = question;
  const { geometries, mapLayers } = data;
  const container = useRef(null);
  const isEmpty = geometries.length === 0;
  const map = useMapboxMap(container, {
    enabled: !isEmpty,
    center: data.center ?? [0, 0],
    zoom: data.zoom ?? 10,
  });
  const drawerState = useAtomValue(drawerStateAtom);

  useEffect(() => {
    if (!map || isEmpty) return;

    const sourceData = {
      type: GEOJSON_FEATURE_COLLECTION,
      features: geometries.map((item) => ({
        type: GEOJSON_FEATURE,
        id: item.id,
        properties: {
          id: item.id,
          comment: item.comment || undefined,
        },
        geometry: item.geometry,
      })),
    };

    if (map.getSource('responses')) {
      map.getSource('responses').setData(sourceData);
    } else {
      map.addSource('responses', { data: sourceData, type: 'geojson' });
    }

    // Add map layer sources
    mapLayers?.forEach((layer) => {
      if (map.getSource(`mapLayers-${layer.id}`)) return;
      map.addSource(`mapLayers-${layer.id}`, { data: layer.geojson, type: 'geojson' });
    });
  }, [map, isEmpty, geometries, mapLayers]);

  useEffect(() => {
    if (!map || isEmpty) return undefined;

    registerCustomPinImages(map, mapLayers, 'mapLayer');

    const layerIds = [];
    const clickHandlers = [];

    // Map layers (POIs / zones)
    mapLayers?.forEach((layer) => {
      const mapLayerSourceId = `mapLayers-${layer.id}`;
      if (get(layer.geojson, 'type') === GEOJSON_POLYGON) {
        const fillId = `mapLayers-${layer.id}`;
        const outlineId = `mapLayers-${layer.id}-outline`;
        map.addLayer({
          id: fillId,
          type: 'fill',
          source: mapLayerSourceId,
          paint: { 'fill-color': '#DB3771', 'fill-opacity': 0.3 },
        });
        map.addLayer({
          id: outlineId,
          type: 'line',
          source: mapLayerSourceId,
          paint: { 'line-color': '#DB3771', 'line-width': 2 },
        });
        layerIds.push(fillId, outlineId);
      }
      if (get(layer.geojson, 'type') === GEOJSON_POINT) {
        const pointId = `mapLayers-${layer.id}-point`;
        map.addLayer({
          id: pointId,
          type: 'symbol',
          source: mapLayerSourceId,
          filter: ['==', '$type', GEOJSON_POINT],
          layout: {
            'icon-image': iconImageForMapLayer(layer.metadata, layer.id),
            'icon-size': 0.8,
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
          },
        });
        layerIds.push(pointId);
        // eslint-disable-next-line max-nested-callbacks
        const handler = (event) => {
          const feature = event.features[0];
          const lngLat = feature.geometry.coordinates;
          new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setText(layer.label).addTo(map);
        };
        map.on('click', pointId, handler);
        clickHandlers.push({ layerId: pointId, handler });
      }
    });

    // Response shape fill layer — low opacity creates natural overlap density effect
    map.addLayer({
      id: 'responses-fill',
      type: 'fill',
      source: 'responses',
      paint: {
        'fill-color': SHAPE_COLOR,
        'fill-opacity': SHAPE_FILL_OPACITY,
      },
    });
    layerIds.push('responses-fill');

    // Response shape outline layer
    map.addLayer({
      id: 'responses-outline',
      type: 'line',
      source: 'responses',
      paint: {
        'line-color': SHAPE_COLOR,
        'line-width': SHAPE_OUTLINE_WIDTH,
        'line-opacity': 0.6,
      },
    });
    layerIds.push('responses-outline');

    // Fit bounds to all geometries if no saved center
    if (!data.center) {
      const bounds = new mapboxgl.LngLatBounds();
      geometries.forEach((item) => {
        item.geometry.coordinates?.[0]?.forEach((coord) => bounds.extend(coord));
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
      }
    }

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      layerIds.forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
    };
  }, [map, isEmpty, geometries, mapLayers, data.center]);

  useEffect(() => {
    map?.resize();
  }, [drawerState]);

  function renderContent() {
    if (isEmpty) {
      return (
        <div style={{ padding: '2rem' }}>
          <EmptyResults hasResponses={hasResponses} />
        </div>
      );
    }

    return <div ref={container} className="engage-results-map" />;
  }

  return <div>{renderContent()}</div>;
}

MapDrawShapeQuestionResults.propTypes = {
  question: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    data: PropTypes.shape({
      center: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
      zoom: PropTypes.number,
      geometries: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          geometry: PropTypes.shape({
            type: PropTypes.string.isRequired,
            coordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)))
              .isRequired,
          }).isRequired,
          comment: PropTypes.string,
        })
      ).isRequired,
      mapLayers: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          geojson: PropTypes.shape({}).isRequired,
        })
      ),
    }).isRequired,
  }).isRequired,
  hasResponses: PropTypes.bool.isRequired,
};

export default MapDrawShapeQuestionResults;
